import React, { useEffect, useRef, useState } from "react";
import { Drawer, DrawerProps, theme } from "antd";

/**
 * Selectors for the things a click may land on without the panel closing.
 *
 * A table is the first: its rows and their action buttons are what put content
 * *into* the panel, so clicking one swaps the preview rather than dismissing
 * it. The rest are the layers antd renders at the end of the document —
 * dropdowns, pickers, dialogs, messages — which belong to whatever is open
 * inside the panel even though they sit outside it in the DOM.
 */
const KEEP_OPEN = [
  ".ant-drawer",
  ".ant-table-wrapper",
  ".ant-select-dropdown",
  ".ant-dropdown",
  ".ant-picker-dropdown",
  ".ant-modal-root",
  ".ant-tooltip",
  ".ant-popover",
  ".ant-message",
  ".ant-notification",
  ".ant-image-preview-root",
  // An escape hatch for anything else that feeds the panel.
  "[data-drawer-keep]",
].join(", ");

/**
 * How a row's preview opens, everywhere in the app.
 *
 * A preview is something you read beside the list, not instead of it: the panel
 * slides in over the right of the screen, the table stays where it was, and the
 * rest of the app keeps working — you can scroll the list, switch a filter or
 * open another row without closing this one first.
 *
 * That is the whole reason it is a Drawer with no mask rather than a Modal. A
 * modal blocks everything behind it, which is right for a decision and wrong
 * for a look. The panel is still told apart from the page by its own shadow,
 * which antd draws from boxShadowDrawer* whether or not there is a mask.
 *
 * Without a mask there is nothing for antd's own maskClosable to listen to, so
 * the click that dismisses it is handled here: anything outside the panel
 * closes it, except the table it was opened from and the popup layers listed
 * above.
 *
 * Use it for any preview opened from a table. Anything that must be answered
 * before the user goes on — a confirmation, a destructive action, a form that
 * would lose data — stays a Modal.
 */
/**
 * The same shadow, cast upward.
 *
 * A bar below its content needs the mirror of the one a bar above it uses, and
 * antd has no token for that: its shadows point down, and the only upward one
 * it ships — boxShadowDrawerDown — is the heavy panel shadow, three times the
 * weight of what a hairline bar wants. The vertical offsets are flipped
 * instead, so the two edges of a panel are the same shadow in opposite
 * directions and both still follow the theme.
 */
const castUpward = (shadow: string) =>
  shadow.replace(
    /(^|,)(\s*)0 (-?[\d.]+)px/g,
    (_match, separator, space, y) =>
      `${separator}${space}0 ${-parseFloat(y)}px`,
  );

export default function PreviewDrawer({
  children,
  open,
  onClose,
  ...props
}: DrawerProps): React.ReactNode {
  const {
    token: { boxShadowTertiary, motionDurationSlow, padding },
  } = theme.useToken();
  /**
   * Whether anything has passed under the title bar.
   *
   * The same rule every bar in this app follows — see Common/StickyBar: the
   * shadow is the only thing saying content is hidden behind the bar rather
   * than ending there, so it must not be painted while nothing is. Answered by
   * a zero-height sentinel at the top of the panel: while it is on screen the
   * panel is at rest, and when it scrolls out of the body the title bar has
   * taken its place. A sentinel rather than the body's scrollTop, because antd
   * owns the scrolling element here and there is nothing to hang a handler on.
   */
  const [scrolled, setScrolled] = useState(false);
  /** Whether anything is still below the actions — the footer's own question. */
  const [more, setMore] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  const endSentinel = useRef<HTMLDivElement>(null);
  const callerStyles =
    typeof props.styles === "function" ? undefined : props.styles;

  useEffect(() => {
    const top = sentinel.current;
    const end = endSentinel.current;

    if (!open || !top || !end) return undefined;

    const observer = new IntersectionObserver((entries) =>
      entries.forEach((entry) => {
        if (entry.target === top) setScrolled(!entry.isIntersecting);
        // The end of the panel out of sight means there is more of it under
        // the actions, which is exactly when the footer has something to cast
        // a shadow onto.
        if (entry.target === end) setMore(!entry.isIntersecting);
      }),
    );

    observer.observe(top);
    observer.observe(end);

    return () => observer.disconnect();
  }, [open]);

  useEffect(() => {
    if (!open || !onClose) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;

      // Capture phase, so a click is judged before the thing it landed on can
      // remove itself from the document — a menu item, say, which would leave
      // nothing to match against by the time the event bubbled back up.
      if (!target?.isConnected || target.closest(KEEP_OPEN)) return;

      onClose(event as unknown as React.MouseEvent<HTMLElement>);
    };

    document.addEventListener("pointerdown", onPointerDown, true);

    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, onClose]);

  return (
    <Drawer
      // No overlay: the app behind stays usable. The shadow is antd's own.
      mask={false}
      // 500px, between antd's two sizes — its default is 378 and its large is
      // 736 — so it is set as a width rather than a size. Every preview in the
      // app opens at this width unless it passes its own.
      width={500}
      destroyOnHidden
      open={open}
      onClose={onClose}
      {...props}
      // After the spread, and merged into whatever the caller passed: a screen
      // that styles the body or the footer keeps that, and the title bar keeps
      // this. antd also accepts `styles` as a function; no screen here uses
      // that form, and one that did would be taking the whole thing over.
      styles={{
        ...callerStyles,
        header: {
          // Above the panel's own content, or the shadow is painted under it.
          // The body comes after the header in the document and anything
          // positioned inside it paints over anything that is not — which is
          // why the shadow was only visible in the margins either side of the
          // letter, where nothing covered it.
          position: "relative",
          zIndex: 1,
          boxShadow: scrolled ? boxShadowTertiary : undefined,
          transition: `box-shadow ${motionDurationSlow}`,
          ...callerStyles?.header,
        },
        // The panel's other edge, and the same rule the other way up: the
        // actions cast a shadow while there is still letter under them, and
        // none once the reader has reached the end.
        footer: {
          position: "relative",
          zIndex: 1,
          // 16 above and below rather than antd's 8. Its footerPaddingBlock is
          // paddingXS, which suits a row of buttons pinned to the bottom of a
          // form; here the row is one of the panel's two edges and sits against
          // a shadow, and 8 left it looking clipped. `padding` is the theme's
          // own 16, not a number written here.
          paddingBlock: padding,
          boxShadow: more ? castUpward(boxShadowTertiary) : undefined,
          transition: `box-shadow ${motionDurationSlow}`,
          ...callerStyles?.footer,
        },
      }}
    >
      <div ref={sentinel} aria-hidden />
      {children}
      <div ref={endSentinel} aria-hidden />
    </Drawer>
  );
}
